import { BackendApi } from "./backend-api";

import { AddImagesToVideoSetRequest, CreateVideoImageSetRequest, VideoImageSet } from "../helpers/interfaces";

export default class VideoCreationApi {
    private backendApi: BackendApi;

    constructor() {
        this.backendApi = BackendApi.Instance;
    }

    /**
     * Creates a new video creation image set
     * @param data The data for creating a video image set
     * @returns The created video image set
     */
    async createVideoImageSet(data: CreateVideoImageSetRequest): Promise<VideoImageSet> {
        return this.backendApi.getApiResponse({
            endPoint: '/video/image-set/create',
            method: 'POST',
            data: data
        });
    }

    /**
     * Adds images to an existing video creation image set
     * @param data The data for adding images to a video set
     * @returns The updated video image set
     */
    async addImagesToVideoSet(data: AddImagesToVideoSetRequest): Promise<VideoImageSet> {
        return this.backendApi.getApiResponse({
            endPoint: '/video/image-set/add-images',
            method: 'POST',
            data: data
        });
    }

    /**
     * Sets the region of interest for an existing video creation image set
     * @param imageSetId The ID of the video creation image set
     * @param regionOfInterest The region of interest object
     * @returns The updated video image set
     */
    async setRoiForVideoImageSet(imageSetId: number, regionOfInterest: any): Promise<any> {
        return this.backendApi.getApiResponse({
            endPoint: '/video/image-set/set-roi',
            method: 'POST',
            data: {
                image_set_id: imageSetId,
                region_of_interest: regionOfInterest
            }
        });
    }

    /**
     * Gets the region of interest for an existing video creation image set
     * @param imageSetId The ID of the video creation image set
     * @returns The region of interest object
     */
    async getRoiForVideoImageSet(imageSetId: number): Promise<any> {
        return this.backendApi.getApiResponse({
            endPoint: `/video/image-set/get-roi/${imageSetId}`,
            method: 'GET'
        });
    }

    async getVideoCreationImageSet(location_id: number, capture_date: string) {
        return this.backendApi.getApiResponse({
            endPoint: '/video/get-video-creation-image-set/loc/' + location_id + '/date/' + capture_date,
            method: 'GET'
        })
    }

    async getVideoCreationImageSetID(location_id: number, capture_date: string) {
        return this.backendApi.getApiResponse({
            endPoint: '/video/get-video-creation-image-set-id/loc/' + location_id + '/date/' + capture_date,
            method: 'GET'
        })
    }

    async createVideoFromDateAndLocation(location_id: number, capture_date: string) {
        let data = {
            "location_id": location_id,
            "capture_date": capture_date,
            
        }
        return this.backendApi.getApiResponse({
            endPoint: '/video/image-set/create-video-from-date-and-location',
            method: 'POST',
            data: data
        })
    }

    async deleteVideoImageSet(imageSetId: number) {
        return this.backendApi.getApiResponse({
            endPoint: '/video/image-set/delete/' + imageSetId,
            method: 'DELETE'
        })
    }


    /**
     * Test function to create a video image set and add images to it
     * This function uses hardcoded values for testing purposes
     * @returns The result of the test operations
     */
    async testVideoCreation(): Promise<{ success: boolean; message: string; imageSetId?: number }> {
        try {
            // Hardcoded test values
            const locationId = 2; // Replace with a valid location ID from your system
            const captureDate = '2024-08-23T00:00:00.000Z';
            const imageIds = [245, 246, 247, 248, 249, 250]; // Replace with valid image IDs from your system
            
            // Step 1: Create a video image set
            const createResponse = await this.createVideoImageSet({
                location_id: locationId,
                capture_date: captureDate,
                region_of_interest: {
                    relative_x: 0.1,
                    relative_y: 0.1,
                    relative_width: 0.8,
                    relative_height: 0.8
                }
            });

            if (!createResponse || !createResponse) {
                return { 
                    success: false, 
                    message: "Failed to create video image set" 
                };
            }
            
            const imageSetId = createResponse.id;
            
            // Step 2: Add images to the video set
            const addImagesResponse = await this.addImagesToVideoSet({
                image_set_id: imageSetId,
                image_ids: imageIds
            });
            
            if (!addImagesResponse) {
                return { 
                    success: false, 
                    message: "Failed to add images to video set",
                    imageSetId: imageSetId
                };
            }
            
            return { 
                success: true, 
                message: "Successfully created video image set and added images",
                imageSetId: imageSetId
            };
        } catch (error) {
            console.error("Error in testVideoCreation:", error);
            return { 
                success: false, 
                message: `Error: ${error instanceof Error ? error.message : String(error)}` 
            };
        }
    }
} 